import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Code, BarChart, CheckCircle, Layout, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const techStack = {
  Languages: [
    { name: 'HTML5', img: 'https://www.svgrepo.com/show/452228/html-5.svg' },
    { name: 'CSS3', img: 'https://www.svgrepo.com/show/452185/css-3.svg' },
    { name: 'JavaScript', img: 'https://www.svgrepo.com/show/452045/js.svg' },
    { name: 'TypeScript', img: 'https://www.svgrepo.com/show/374146/typescript-official.svg' },
    { name: 'PHP', img: 'https://www.svgrepo.com/show/452088/php.svg', status: 'familiar' },
    { name: 'Python', img: 'https://www.svgrepo.com/show/452091/python.svg', status: 'familiar' },
    { name: 'SQL', img: 'https://www.svgrepo.com/show/331760/sql-database-generic.svg' },
  ],
  Frontend: [
    { name: 'React', img: 'https://www.svgrepo.com/show/452092/react.svg' },
    { name: 'Next.js', img: 'https://www.svgrepo.com/show/342062/next-js.svg' },
    { name: 'Vue.js', img: 'https://www.svgrepo.com/show/452130/vue.svg', status: 'familiar' },
    { name: 'Tailwind', img: 'https://www.svgrepo.com/show/374118/tailwind.svg' },
    { name: 'Bootstrap', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bootstrap/bootstrap-original.svg' },
    { name: 'Sass', img: 'https://www.svgrepo.com/show/354310/sass.svg', status: 'familiar' },
    { name: 'Material UI', img: 'https://www.svgrepo.com/show/354048/material-ui.svg', status: 'learning' },
  ],
  Backend: [
    { name: 'Laravel', img: 'https://www.svgrepo.com/show/353985/laravel.svg', status: 'familiar' },
    { name: 'Node.js', img: 'https://www.svgrepo.com/show/452075/node-js.svg', status: 'familiar' },
    { name: 'Express', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg', status: 'familiar' },
    { name: 'NestJS', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nestjs/nestjs-original.svg', status: 'learning' },
  ],
  Databases: [
    { name: 'MySQL', img: 'https://www.svgrepo.com/show/373848/mysql.svg' },
    { name: 'PostgreSQL', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
    { name: 'MariaDB', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mariadb/mariadb-original.svg' },
    { name: 'SQLite', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/sqlite/sqlite-original.svg' },
    { name: 'Firebase', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg', status: 'familiar' },
    { name: 'MongoDB', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg', status: 'familiar' },
  ],
  "Data & Analytics": [
    { name: 'Tableau', img: 'https://cdn.worldvectorlogo.com/logos/tableau-software.svg' },
    { name: 'Power BI', img: 'https://upload.wikimedia.org/wikipedia/commons/c/cf/New_Power_BI_Logo.svg' },
    { name: 'Looker Studio', img: 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_data_studio.svg', status: 'familiar' },
    { name: 'Microsoft Excel', img: 'https://cdn.worldvectorlogo.com/logos/microsoft-excel-2013.svg' },
    { name: 'Google Sheets', img: 'https://www.gstatic.com/images/branding/product/2x/sheets_2020q4_48dp.png' },
  ],
  QA: [
    { name: 'Cypress', img: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cypress.svg', status: 'familiar' },
    { name: 'Postman', img: 'https://www.vectorlogo.zone/logos/getpostman/getpostman-icon.svg' },
    { name: 'Jest', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jest/jest-plain.svg', status: 'familiar' },
  ],
  "UI/UX & Design": [
    { name: 'Figma', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg' },
    { name: 'Canva', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/canva/canva-original.svg' },
  ],
  "Collaboration & Tools": [
    { name: 'VS Code', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg' },
    { name: 'Github', img: 'https://www.svgrepo.com/show/512317/github-142.svg' },
    { name: 'Git', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
    { name: 'Jira', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg', status: 'familiar' },
    { name: 'Trello', img: 'https://www.svgrepo.com/show/475688/trello-color.svg', status: 'familiar' },
    { name: 'ClickUp', img: 'https://www.svgrepo.com/show/331339/clickup.svg', status: 'familiar' },
    { name: 'Notion', img: 'https://upload.wikimedia.org/wikipedia/commons/e/e9/Notion-logo.svg' },
    { name: 'Microsoft Teams', img: 'https://www.svgrepo.com/show/452111/teams.svg' },
    { name: 'Google Meet', img: 'https://www.svgrepo.com/show/452220/google-meet.svg' },
    { name: 'zoom', img: 'https://www.svgrepo.com/show/452140/zoom.svg' },
    { name: 'Microsoft Office', img: 'https://cdn.worldvectorlogo.com/logos/microsoft-office-2013.svg' },
    { name: 'Google Workspace', img: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg' },
  ],
  "DevOps & Deploy": [
    { name: 'CI/CD', img: 'https://www.svgrepo.com/show/372275/ci-cd.svg', status: 'familiar' },
    { name: 'Docker', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg', status: 'familiar' },
    { name: 'Jenkins', img: 'https://www.svgrepo.com/show/373699/jenkins.svg', status: 'learning' },
    { name: 'Kubernetes', img: 'https://www.svgrepo.com/show/448233/kubernetes.svg', status: 'learning' },
    { name: 'Netlify', img: 'https://www.svgrepo.com/show/373874/netlify.svg' },
    { name: 'Vercel', img: 'https://www.svgrepo.com/show/354513/vercel-icon.svg' },
  ],
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const About = () => {
  const ref = useRef(null);
  const [currentSection, setCurrentSection] = useState(0); // Index of current section
  const [currentAboutPage, setCurrentAboutPage] = useState(0); // For Who I Am pagination

  const sections = Object.keys(techStack);
  const totalSections = sections.length;

  // About content pages
  const aboutPages = [
    {
      type: 'content',
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Hi, I'm <span className="text-blue-400 font-semibold">Oktaryan Hugo Wiratama</span> — 
            a <span className="text-purple-400 font-semibold">Front End Developer</span> who builds 
            clean, responsive, and user-friendly web applications.  
            I mainly work with 
            <span className="text-blue-400 font-semibold"> React, Next.js, and Tailwind CSS</span>, 
            but I also enjoy exploring other modern tools to stay sharp and adaptive.
          </p>
          <p>
            Beyond Front End, I dive into 
            <span className="text-purple-400 font-semibold"> Data Analytics</span>, <span className="text-purple-400 font-semibold">Software QA</span>, 
            and <span className="text-purple-400 font-semibold">UX Design</span>.
            Also have basic knowledge in 
            <span className="text-purple-400 font-semibold"> Digital Marketing</span>, especially in <span className="text-blue-400 font-semibold">Social Media Ads</span>.  
            I love turning raw data into insights, ensuring product quality through testing, 
            and crafting intuitive experiences that truly connect with users.
          </p>
        </div>
      )
    },
    {
      type: 'interests',
      content: null // Will be handled separately
    }
  ];

  const totalAboutPages = aboutPages.length;

  // Navigation functions 
  const goToNextSection = () => {
    setCurrentSection(prev => prev >= totalSections - 1 ? 0 : prev + 1);
  };

  const goToPrevSection = () => {
    setCurrentSection(prev => prev <= 0 ? totalSections - 1 : prev - 1);
  };

  const goToSection = (sectionIndex) => {
    setCurrentSection(sectionIndex);
  };

  // About navigation functions
  const goToNextAboutPage = () => {
    setCurrentAboutPage(prev => prev >= totalAboutPages - 1 ? 0 : prev + 1);
  };

  // Get current section data
  const currentSectionName = sections[currentSection];
  const currentItems = techStack[currentSectionName];

  const interests = [
    { icon: <Code className="w-6 h-6" />, title: 'Frontend Development', desc: 'Building responsive, modern, and user-friendly web interfaces' },
    { icon: <BarChart className="w-6 h-6" />, title: 'Data Analytics', desc: 'Transforming raw data into insights through cleaning and visualization' },
    { icon: <CheckCircle className="w-6 h-6" />, title: 'Software QA', desc: 'Ensuring quality through SDLC, STLC, and structured testing' },
    { icon: <Layout className="w-6 h-6" />, title: 'UX Design', desc: 'Designing seamless and intuitive user experiences' },
  ];  

  return (
    <section
      id="about"
      className="min-h-screen py-20 bg-gradient-to-tr from-gray-900 via-slate-900 to-gray-900 text-white relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
      </div>

      {/* Container fix: center align + balanced padding */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="space-y-16"
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
              About Me
            </h2>
          </motion.div>

          {/* Grid wrapper fix: justify-items-center for mobile, stretch for desktop */}
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-7xl mx-auto">
            
            {/* About Content */}
            <motion.div variants={itemVariants} className="w-full">
              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-semibold text-blue-400">Who Am I</h3>

                  {/* About Navigation */}
                  <div className="flex items-center gap-2">
                    <motion.button
                      onClick={goToNextAboutPage}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-3 bg-gray-700/50 hover:bg-gray-600/50 rounded-xl border border-gray-600/50"
                    >
                      <ChevronDown className="w-4 h-4 text-blue-400 hover:text-blue-300" />
                    </motion.button>
                  </div>
                </div>

                {/* About Content with Pagination */}
                <div className="relative">
                  <motion.div
                    key={currentAboutPage}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="min-h-[400px]"
                  >
                    {currentAboutPage === 0 ? (
                      // Page 1
                      <div className="w-full space-y-6">
                        {/* About Text */}
                        <div>
                          <h4 className="text-lg font-semibold mb-4 text-gray-300">About</h4>
                          <div className="space-y-4 text-gray-300 leading-relaxed">
                            <p>
                              Hi, I'm <span className="text-blue-400 font-semibold">Oktaryan Hugo Wiratama</span> — 
                              a <span className="text-purple-400 font-semibold">Front End Developer</span> who builds 
                              clean, responsive, and user-friendly web applications.  
                              I mainly work with 
                              <span className="text-blue-400 font-semibold"> React, Next.js, and Tailwind CSS</span>, 
                              but I also enjoy exploring other modern tools to stay sharp and adaptive.
                            </p>
                            <p>
                              Beyond Front End, I dive into 
                              <span className="text-purple-400 font-semibold"> Data Analytics</span>, <span className="text-purple-400 font-semibold">Software QA</span>, 
                              and <span className="text-purple-400 font-semibold">UX Design</span>.
                              Also have basic knowledge in 
                              <span className="text-purple-400 font-semibold"> Digital Marketing</span>, especially in <span className="text-blue-400 font-semibold">Social Media Ads</span>.  
                              I love turning raw data into insights, ensuring product quality through testing, 
                              and crafting intuitive experiences that truly connect with users.
                            </p>
                            <p>
                              As a <span className="text-blue-400 font-semibold">fresh graduate</span>, I’m actively seeking new career opportunities 
                              while continuously improving myself through <span className="text-purple-400 font-semibold">personal projects</span>, 
                              <span className="text-purple-400 font-semibold"> online courses</span>, 
                              <span className="text-purple-400 font-semibold"> bootcamp programs</span>, and 
                              <span className="text-purple-400 font-semibold"> webinar trainings</span>.  
                            </p>
                          </div>
                        </div>

                        {/* Environment */}
                        <div className="pt-6 border-t border-gray-700/50">
                          <h4 className="text-lg font-semibold mb-4 text-gray-300">Development Environment</h4>
                          <div className="flex items-center gap-6 text-gray-400">
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <img
                                src="https://www.svgrepo.com/show/452062/microsoft.svg"
                                alt="Windows"
                                className="w-6 h-6"
                              />
                              <span>Windows</span>
                            </motion.div>
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <img
                                src="https://www.svgrepo.com/show/452122/ubuntu.svg"
                                alt="Linux"
                                className="w-6 h-6"
                              />
                              <span>Linux</span>
                            </motion.div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Page 2
                      <div className="w-full">
                        <h4 className="text-lg font-semibold mb-6 text-gray-300">My Interests</h4>
                        <div className="grid gap-6">
                          {interests.map((interest, index) => (
                            <motion.div
                              key={`interest-${index}`}
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.25 }} 
                              whileHover={{
                                scale: 1.05,
                                borderColor: "rgba(255,255,255,0.2)",
                                transition: { duration: 0.15 } 
                              }}
                              className="bg-gray-700/30 backdrop-blur-sm border border-gray-600/30 rounded-xl shadow-lg p-6"
                            >
                              <div className="flex items-center gap-4">
                                <div className="text-2xl text-blue-400">{interest.icon}</div>
                                <div>
                                  <h5 className="font-semibold text-white">{interest.title}</h5>
                                  <p className="text-gray-400 text-sm">{interest.desc}</p>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Tech Stack */}
            <motion.div variants={itemVariants} className="w-full">
              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 shadow-2xl">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-2xl font-semibold text-purple-400">Tech Stack</h3>
                  <div className="text-sm text-gray-400">
                    {currentSection + 1} / {totalSections}
                  </div>
                </div>

                <div className="space-y-6">
                  <h4 className="text-lg font-semibold text-gray-300">{currentSectionName}</h4>

                  {/* Tech Grid */}
                  <motion.div
                    key={currentSectionName}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                  >
                    {currentItems.map((item, idx) => (
                      <motion.div
                        key={`${item.name}-${idx}`}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.25 }}
                        whileHover={{
                          scale: 1.1,
                          y: -5,
                          backgroundColor: "rgba(107,114,128,0.5)",
                          borderColor: "rgba(255,255,255,0.2)",
                          transition: { duration: 0.15 }
                        }}
                        className="relative bg-gray-600/50 border border-gray-500/50 rounded-xl cursor-pointer w-full h-28 flex flex-col items-center justify-center"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <img
                            src={item.img}
                            alt={item.name}
                            className="w-10 h-10 object-contain"
                          />
                          <span className="text-xs text-gray-400 text-center font-medium">
                            {item.name}
                          </span>
                        </div>

                        {item.status && (
                          <div
                            className={`absolute -top-1 -right-1 px-1.5 py-0.5 rounded-md text-[8px] font-semibold
                              ${item.status === 'learning' ? 'bg-yellow-500/80 text-black' : ''}
                              ${item.status === 'familiar' ? 'bg-blue-500/80 text-white' : ''}
                            `}
                          >
                            {item.status}
                          </div>
                        )}
                      </motion.div>
                    ))}

                    {/* Placeholder */}
                    {Array.from({ length: Math.max(0, 12 - currentItems.length) }, (_, index) => (
                      <div
                        key={`placeholder-${currentSectionName}-${index}`}
                        className="invisible w-full h-28"
                      />
                    ))}
                  </motion.div>
                </div>

                {/* Section Navigation */}
                <div className="flex justify-center items-center gap-4 mt-8">
                  <motion.button
                    onClick={goToPrevSection}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-3 bg-gray-700/80 hover:bg-gray-600/80 rounded-full border border-gray-600/50 backdrop-blur-sm"
                  >
                    <ChevronLeft className="w-5 h-5 text-purple-400 hover:text-purple-300" />
                  </motion.button>

                  <div className="flex items-center gap-3">
                    {sections.map((section, index) => (
                      <motion.button
                        key={section}
                        onClick={() => goToSection(index)}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        className={`w-3 h-3 rounded-full ${
                          currentSection === index
                            ? 'bg-purple-500 scale-125'
                            : 'bg-gray-600 hover:bg-gray-500'
                        }`}
                      />
                    ))}
                  </div>

                  <motion.button
                    onClick={goToNextSection}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-3 bg-gray-700/80 hover:bg-gray-600/80 rounded-full border border-gray-600/50 backdrop-blur-sm"
                  >
                    <ChevronRight className="w-5 h-5 text-purple-400 hover:text-purple-300" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;